// data-layer/mutations/auth.ts

import type { AxiosError, AxiosProgressEvent } from 'axios';
import type { ApiImportData, ApiResponse, Vehicle, VehicleListParams, VehiclePayload } from '@dc-trans/data-layer';
import { createVehicleData, exportVehicleData, importVehicleData, updateVehicleData } from '@dc-trans/data-layer';
import { useMutation } from '@tanstack/react-query';
import { mergeApiErrorNotificationMeta, type MutationOptionsWithNotification } from '@/mutations/utils';

// The custom hook that you will use in your components
export const useCreateVehicleMutation = (
  options?: MutationOptionsWithNotification<
    ApiResponse<Vehicle>,
    AxiosError<ApiResponse<Vehicle>>,
    Partial<VehiclePayload>
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal memproses data kendaraan.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<ApiResponse<Vehicle>, AxiosError<ApiResponse<Vehicle>>, Partial<VehiclePayload>>({
    ...restOptions,
    mutationFn: createVehicleData,
    meta: mergedMeta,
  });
};

export const useUpdateVehicleMutation = (
  options?: MutationOptionsWithNotification<
    ApiResponse<Vehicle>,
    AxiosError<ApiResponse<Vehicle>>,
    Partial<VehiclePayload>
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal memperbarui data kendaraan.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<ApiResponse<Vehicle>, AxiosError<ApiResponse<Vehicle>>, Partial<VehiclePayload>>({
    ...restOptions,
    mutationFn: updateVehicleData,
    meta: mergedMeta,
  });
};

// In data-layer: Create a mutation hook
export const useDownloadVehicleData = (
  options?: MutationOptionsWithNotification<
    {
      data: any;
      filename: string;
    },
    AxiosError<{
      data: any;
      filename: string;
    }>,
    VehicleListParams
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal mengunduh data kendaraan.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<
    {
      data: any;
      filename: string;
    },
    AxiosError<{
      data: any;
      filename: string;
    }>,
    VehicleListParams
  >({
    ...restOptions,
    mutationFn: exportVehicleData,
    meta: mergedMeta,
  });
};

export const useUploadVehicleData = (
  options?: MutationOptionsWithNotification<
    ApiResponse<ApiImportData>,
    AxiosError<ApiResponse<ApiImportData>>,
    {
      data: FormData;
      progressCallback: (_progressEvent: AxiosProgressEvent) => void;
    }
  >
) => {
  const { disableErrorNotification, meta, ...restOptions } = options ?? {};
  const mergedMeta = mergeApiErrorNotificationMeta(
    {
      message: 'Gagal mengunggah data kendaraan.',
    },
    meta,
    disableErrorNotification
  );

  return useMutation<
    ApiResponse<ApiImportData>,
    AxiosError<ApiResponse<ApiImportData>>,
    {
      data: FormData;
      progressCallback: (_progressEvent: AxiosProgressEvent) => void;
    }
  >({
    ...restOptions,
    mutationFn: importVehicleData,
    meta: mergedMeta,
  });
};
